import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { CalendarGrid } from "../components/CalendarGrid";
import { ErrorState, LoadingState } from "../components/LoadingState";
import type { CalendarResponse } from "../types";
import { formatCurrency } from "../utils/currency";

export function CalendarPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<CalendarResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadCalendar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await api.getCalendar(year, month));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "日历加载失败。");
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    void loadCalendar();
    const refresh = () => void loadCalendar();
    window.addEventListener("ledger-data-changed", refresh);
    return () => window.removeEventListener("ledger-data-changed", refresh);
  }, [loadCalendar]);

  const changeMonth = (delta: number) => {
    const next = new Date(year, month - 1 + delta, 1);
    setYear(next.getFullYear());
    setMonth(next.getMonth() + 1);
  };

  const summary = useMemo(() => data?.days.reduce(
    (total, day) => ({
      expense: total.expense + day.expense_cents,
      income: total.income + day.income_cents,
    }),
    { expense: 0, income: 0 },
  ) ?? { expense: 0, income: 0 }, [data]);

  return (
    <main className="page calendar-page">
      <section className="calendar-heading">
        <div>
          <p className="eyebrow">我的账本</p>
          <h1>支出日历</h1>
        </div>
        <Link className="limit-chip" to="/settings">
          <SlidersHorizontal size={15} />
          每日界限 {formatCurrency(data?.daily_expense_limit_cents ?? 10000)}
        </Link>
      </section>

      <section className="summary-strip" aria-label="本月收支汇总">
        <div><span>本月支出</span><strong className="expense">{formatCurrency(summary.expense)}</strong></div>
        <div><span>本月收入</span><strong className="income">{formatCurrency(summary.income)}</strong></div>
        <div><span>本月结余</span><strong>{formatCurrency(summary.income - summary.expense)}</strong></div>
      </section>

      <section className="calendar-card">
        <div className="month-switcher">
          <button className="icon-button" onClick={() => changeMonth(-1)} aria-label="上个月"><ChevronLeft /></button>
          <h2>{year}年 {month}月</h2>
          <button className="icon-button" onClick={() => changeMonth(1)} aria-label="下个月"><ChevronRight /></button>
        </div>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={() => void loadCalendar()} />
        ) : data ? (
          <CalendarGrid year={year} month={month} days={data.days} limitCents={data.daily_expense_limit_cents} />
        ) : null}
        <div className="legend" aria-label="颜色说明">
          <span><i className="within" />界限内</span>
          <span><i className="over" />超出界限</span>
          <span><i className="empty" />无支出</span>
        </div>
      </section>
    </main>
  );
}
